import { Injectable } from '@angular/core';
import { cognitoConfig, getCognitoLogoutUrl, getCognitoTokenUrl } from '../auth/cognito.config';

interface CognitoRefreshTokenResponse {
  access_token: string;
  id_token?: string;
  refresh_token?: string;
}

const ACCESS_TOKEN_KEY = 'access_token';
const ID_TOKEN_KEY = 'id_token';
const REFRESH_TOKEN_KEY = 'refresh_token';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private refreshPromise: Promise<string | null> | null = null;

  getUsername(): string {
    return localStorage.getItem('user_name') ?? '';
  }

  getAccessToken(): string | null {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  }

  saveTokens(tokens: { accessToken: string; idToken?: string; refreshToken?: string }): void {
    localStorage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken);

    if (tokens.idToken) {
      localStorage.setItem(ID_TOKEN_KEY, tokens.idToken);
    }

    if (tokens.refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
    }
  }

  async getValidAccessToken(): Promise<string | null> {
    const accessToken = this.getAccessToken();
    if (accessToken && !this.isJwtExpired(accessToken, cognitoConfig.refreshLeewaySeconds)) {
      return accessToken;
    }

    if (!cognitoConfig.useRefreshToken) {
      return null;
    }

    return this.refreshAccessToken();
  }

  private async refreshAccessToken(): Promise<string | null> {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = this.performRefreshTokenRequest();
    try {
      return await this.refreshPromise;
    } finally {
      this.refreshPromise = null;
    }
  }

  private async performRefreshTokenRequest(): Promise<string | null> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return null;
    }

    const body = new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: cognitoConfig.clientId,
      refresh_token: refreshToken,
    });

    try {
      const response = await fetch(getCognitoTokenUrl(cognitoConfig), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: body.toString(),
      });

      if (!response.ok) {
        return null;
      }

      const tokens = (await response.json()) as CognitoRefreshTokenResponse;
      if (!tokens.access_token) {
        return null;
      }

      this.saveTokens({
        accessToken: tokens.access_token,
        idToken: tokens.id_token,
        refreshToken: tokens.refresh_token || refreshToken,
      });

      return tokens.access_token;
    } catch {
      return null;
    }
  }

  private isJwtExpired(token: string, leewaySeconds = 0): boolean {
    const parts = token.split('.');
    if (parts.length < 2) {
      return true;
    }

    try {
      const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
      const exp = Number(payload?.exp);
      if (!Number.isFinite(exp)) {
        return true;
      }

      const nowInSeconds = Math.floor(Date.now() / 1000);
      return exp <= nowInSeconds + Math.max(0, leewaySeconds);
    } catch {
      return true;
    }
  }

  logout(): void {
    localStorage.clear();
    const logoutUrl = getCognitoLogoutUrl(cognitoConfig);
    window.location.href = logoutUrl;
  }
}
